import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Ghost,
  BarChart2,
  FileText,
  Key,
  Inbox,
  Link2,
  Globe,
  Lightbulb,
  Star,
} from "lucide-react";
import Footer from "../components/Footer";
import NavBar from "../components/NavBar";
import useAuthStore from "../store/authStore";
import PublicNav from "../components/PublicNav";
import HowJoborigoWorks from "../components/HowJoborigoWorks";

const STEPS = [
  {
    number: "01",
    title: "Create your free account",
    description:
      "Register in under a minute. No credit card required. Joborigo is free for job seekers, forever.",
    tip: null,
  },
  {
    number: "02",
    title: "Add your applications",
    description:
      "Every time you apply for a job, add it to Joborigo. Type the company name and Joborigo looks up their current openings, so picking the role fills in the title, link and location for you. You can always enter the details by hand instead. Applications can also arrive automatically when an employer pushes status updates through the Joborigo API.",
    tip: null,
  },
  {
    number: "03",
    title: "Build your resume timeline",
    description:
      "Add your work experience, education, certifications and projects once. From that single timeline you can build several resumes, each tailored to a different kind of role. Every resume you make shareable gets its own permanent link.",
    tip: "Mark one resume as your primary. That is the one your general link points at.",
  },
  {
    number: "04",
    title: "Paste the right link when you apply",
    description:
      "Wherever you apply, put your Joborigo resume link in the portfolio, website or other link field. For a specific role, copy that resume's own link, since it stays fixed at the version you actually sent. Your general link is for speculative applications and email signatures, where you want it to stay current.",
    tip: "Employers who open either link land on your resume with a one-click option to send you a job position and connect their hiring system. Every application you send becomes a quiet invitation for an employer to close the loop with you.",
  },
  {
    number: "05",
    title: "Add a connect link for direct integration",
    description:
      "For each application you can also generate a one-time connect link from your dashboard. Paste it alongside your resume link. Unlike a resume link, which is public and lasting, the connect link is private, expires in 72 hours and can only be used once.",
    tip: "If the employer's hiring system supports it, the connect link lets them attach status updates directly to your application without ever seeing your email address. If they have no integration, the link brings them to a page explaining Joborigo and inviting them to connect by hand.",
  },
  {
    number: "06",
    title: "Track every status update",
    description:
      "As applications progress, update the status in Joborigo: Applied, Screening, Interview, Offer, Rejected. Where the employer uses the Joborigo API, those updates arrive on their own. Either way your dashboard reflects the real state of your search.",
    tip: null,
  },
  {
    number: "07",
    title: "Let ghost detection do the work",
    description:
      "Joborigo watches every application for inactivity. When an employer goes quiet for longer than the stage would suggest, the application is flagged. You stop wondering which employers have gone silent and which are still live.",
    tip: null,
  },
  {
    number: "08",
    title: "Opt into the candidate arena (optional)",
    description:
      "If you want employers to find you, enable your arena profile. It shows your skills, availability, location preference and salary expectations without your name, gender or photo. Opting in is entirely your choice and you can turn it off at any time.",
    tip: null,
  },
];

const FEATURES = [
  {
    icon: Ghost,
    title: "Ghost detection",
    description:
      "Joborigo tracks how long each application has been inactive relative to its current stage. Applications that go cold are flagged automatically, so you know where to put your energy and when to move on.",
  },
  {
    icon: BarChart2,
    title: "Job search analytics",
    description:
      "Your whole search at a glance: applications sent, response rate, how long employers typically take, and which are actually moving. Useful for deciding what to do next rather than guessing.",
  },
  {
    icon: FileText,
    title: "Resume builder",
    description:
      "One timeline, several tailored resumes. Each shareable resume gets its own permanent link and QR code, and your primary resume is the one your general link points at.",
  },
  {
    icon: Key,
    title: "Connect link",
    description:
      "A private, one-time link you generate per application. Paste it alongside your resume link when you apply. Employers who use it can connect their hiring system and send you real-time updates without seeing your email address. Expires in 72 hours, one use only.",
  },
  {
    icon: Inbox,
    title: "Employer inbox",
    description:
      "When an employer finds you through the arena or a resume link and sends you a job position, it arrives in your Joborigo inbox. Review the role, the salary and the process, then import it as an application in one click.",
  },
  {
    icon: Link2,
    title: "Employer API integration",
    description:
      "Employers can connect their hiring system to Joborigo and push status updates straight to you. When something changes, an interview scheduled, an offer extended, a decision made, you hear about it without chasing.",
  },
  {
    icon: Globe,
    title: "Anonymous candidate arena",
    description:
      "Opt in and let employers find you without revealing who you are. Your arena profile shows skills, availability and preferences only. You write your own headline and summary, so keep contact details out of them unless you want employers to see them.",
  },
];

const FAQ = [
  {
    q: "What is the difference between my general resume link and a specific resume link?",
    a: "Your general link is tied to your account and always points at whichever resume you have marked as primary, so it stays current as you update your work. That makes it right for an email signature or a speculative approach. Each individual resume also has its own permanent link, which stays fixed at that version. Use the specific link when you apply for a particular role, so an employer opening it months later sees the resume you actually sent rather than whatever is newest.",
  },
  {
    q: "What is the connect link then?",
    a: "A resume link shows an employer your resume. A connect link lets their hiring system attach itself to one specific application of yours, so status updates flow back to you without your email address being shared. It is private, expires in 72 hours and works once. Resume links are for being read. Connect links are for closing the loop.",
  },
  {
    q: "Can employers see my notes?",
    a: "Never. Your notes are private and are never shared with employers. Messages from employers and your own notes are kept completely separate.",
  },
  {
    q: "What does an employer see when they open my resume link?",
    a: "Your published resume: name, summary and the timeline entries you chose to include on that resume. They do not see your notes, your other applications, your ghost detection data or anything else from your account.",
  },
  {
    q: "What happens when an employer clicks my connect link?",
    a: "If their system integrates with Joborigo, it can use the token to attach to your application and send status updates automatically. If it does not, they land on a page explaining what Joborigo is and how to connect by hand. Either way your email address stays private.",
  },
  {
    q: "Do I have to opt into the arena?",
    a: "No. The arena is entirely optional and off unless you enable it. Your resume links work regardless.",
  },
  {
    q: "What is ghost detection exactly?",
    a: "It monitors how long each application has been inactive. When an employer stops responding for longer than is normal at that stage, Joborigo flags it. You can see at a glance which applications are live, which have gone cold and which are formally closed.",
  },
  {
    q: "Is Joborigo free?",
    a: "Yes, completely free for job seekers. No trial, no paywall, no limits. Unlimited applications, status updates, ghost detection, resume builder, LinkedIn import and analytics, free forever. Agencies and employers pay for their side of the platform.",
  },
  {
    q: "What if the employer does not use the Joborigo API?",
    a: "Most will not, at least not yet. That is fine. You update statuses yourself, much as you would in a spreadsheet, but with ghost detection and analytics built in. As more employers join, more updates arrive on their own.",
  },
];

export default function HowItWorks() {
  const seekerUser = useAuthStore((s) => s.seekerUser);

  useEffect(() => {
    document.title = "Joborigo: How it works";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      {seekerUser ? <NavBar /> : <PublicNav />}
      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        {/* Hero */}
        <section className="mb-16 text-center">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            How it works
          </p>
          <h1 className="text-4xl font-extrabold mb-4 leading-tight">
            Your job search,
            <br />
            finally under control.
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed max-w-xl mx-auto">
            Joborigo is more than an application tracker. Used well, it gives
            you full visibility into your search and quietly works in the
            background to bring employers to you.
          </p>
          {!seekerUser && (
            <Link
              to="/register"
              className="inline-block mt-8 bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
            >
              Get started free
            </Link>
          )}
        </section>

        <div className="border-t border-gray-100 dark:border-white/10 mb-16" />

        {/* How Joborigo works, diagram with flow toggle */}
        <HowJoborigoWorks />

        <div className="border-t border-gray-100 dark:border-white/10 mb-16" />

        {/* Steps */}
        <section className="mb-16">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            The optimised job search
          </p>
          <h2 className="text-2xl font-bold mb-10">
            How to get the most out of Joborigo
          </h2>
          <div className="flex flex-col gap-10">
            {STEPS.map((step) => (
              <div key={step.number} className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-brand-violet dark:text-brand-violet-on-dark">
                    {step.number}
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm mb-2">
                    {step.description}
                  </p>
                  {step.tip && (
                    <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3 mt-3">
                      <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark leading-relaxed">
                        <span className="font-semibold inline-flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" /> Pro tip:{" "}
                        </span>
                        {step.tip}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="border-t border-gray-100 dark:border-white/10 mb-16" />

        {/* Three links callout. This is the part users get confused about,
            so each link gets its own row with the distinction stated plainly. */}
        <section className="mb-16">
          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-8 py-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Three links, three jobs
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">
                Joborigo gives you more than one link. They are not
                interchangeable, and picking the right one takes a second.
              </p>

              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      Your general link, always current
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                      Tied to your account and pointed at whichever resume you
                      have marked primary. Update that resume and the link
                      follows. Use it in an email signature, on a business card,
                      or for a speculative approach.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      A resume's own link, fixed
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                      Every shareable resume has one, and it always shows that
                      resume. Use it when you apply for a specific role, so an
                      employer opening it in three months sees the version you
                      actually sent rather than whatever is newest.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center flex-shrink-0">
                    <Key className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      Connect link, private and one-time
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                      Generated per application. Expires in 72 hours and works
                      once. It lets an employer's hiring system attach to that
                      application and send you status updates without your email
                      address being shared.
                    </p>
                  </div>
                </div>

                <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3">
                  <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark leading-relaxed">
                    <span className="font-semibold inline-flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Best practice:{" "}
                    </span>
                    When you apply for a specific role, paste that resume's own
                    link plus a connect link in the portfolio or website field.
                    The resume link gets you read. The connect link gets you
                    told what happened.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="border-t border-gray-100 dark:border-white/10 mb-16" />

        {/* Features */}
        <section className="mb-16">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            Features
          </p>
          <h2 className="text-2xl font-bold mb-8">
            Everything in Joborigo, explained
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5"
              >
                <div className="w-9 h-9 rounded-xl bg-brand-violet/10 flex items-center justify-center mb-3">
                  <feature.icon className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="border-t border-gray-100 dark:border-white/10 mb-16" />

        {/* FAQ */}
        <section className="mb-16">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            FAQ
          </p>
          <h2 className="text-2xl font-bold mb-8">Common questions</h2>
          <div className="flex flex-col gap-6">
            {FAQ.map((item) => (
              <div
                key={item.q}
                className="border-b border-gray-100 dark:border-white/10 pb-6 last:border-0 last:pb-0"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {item.q}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        {!seekerUser && (
          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-8 py-10 text-center">
              <h2 className="text-2xl font-bold mb-2">
                Ready to take control?
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Free to start. No credit card required.
              </p>
              <Link
                to="/register"
                className="inline-block bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
              >
                Create your free account
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
